import React from 'react';
import { injectIntl } from 'react-intl';

import PropTypes from 'prop-types';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import { ChatCardContainer } from '../../Dashboard/styles';

function formatM2(value) {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toFixed(2)} m²`;
}

function CleaningReportAreaComparisonCard({
    primaryColor,
    secondaryColor,
    data,
    intl,
}) {
    const tooltipLabelForSeries = seriesName => {
        if (seriesName === 'clean_area') {
            return intl.formatMessage({
                id: 'page.robots.cleaning_report.legend_clean_area',
            });
        }
        if (seriesName === 'task_area') {
            return intl.formatMessage({
                id: 'page.robots.cleaning_report.legend_task_area',
            });
        }
        return seriesName;
    };

    return (
        <div className="row card-row">
            <div className="col-12 mb-5 dash-card">
                <ChatCardContainer>
                    <ChartCard
                        className="animated slideInFromLeft animation-duration-3"
                        title={
                            <LocaleMessage msg="page.robots.cleaning_report.area_compare_title" />
                        }
                        desc={
                            <LocaleMessage msg="page.robots.cleaning_report.area_compare_subtitle" />
                        }
                        value=""
                    >
                        <div className="row">
                            <div className="col-12 mb-3 area-chart-container">
                                <ResponsiveContainer
                                    className="card-img-bottom overflow-hidden animated slideInFromLeft animation-duration-5"
                                    width="100%"
                                    height={320}
                                >
                                    <ComposedChart
                                        data={data}
                                        margin={{
                                            top: 24,
                                            right: 16,
                                            left: 8,
                                            bottom: 8,
                                        }}
                                    >
                                        <CartesianGrid vertical={false} />
                                        <XAxis dataKey="date" />
                                        <YAxis
                                            tickFormatter={v =>
                                                `${Number(v).toFixed(0)}`
                                            }
                                            label={{
                                                value: 'm²',
                                                angle: -90,
                                                position: 'insideLeft',
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(v, name) => [
                                                formatM2(v),
                                                tooltipLabelForSeries(name),
                                            ]}
                                            labelFormatter={label => label}
                                        />
                                        <Legend
                                            content={({ payload }) => (
                                                <ul
                                                    className="recharts-default-legend"
                                                    style={{
                                                        textAlign: 'center',
                                                        padding: 0,
                                                        margin: '0 0 8px',
                                                        listStyle: 'none',
                                                    }}
                                                >
                                                    {payload.map(entry => (
                                                        <li
                                                            key={entry.dataKey}
                                                            style={{
                                                                display:
                                                                    'inline-block',
                                                                marginRight: 20,
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    color:
                                                                        entry.color,
                                                                    marginRight: 6,
                                                                }}
                                                            >
                                                                ●
                                                            </span>
                                                            {entry.dataKey ===
                                                            'clean_area' ? (
                                                                <LocaleMessage msg="page.robots.cleaning_report.legend_clean_area" />
                                                            ) : (
                                                                <LocaleMessage msg="page.robots.cleaning_report.legend_task_area" />
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        />
                                        <Bar
                                            dataKey="task_area"
                                            name="task_area"
                                            fill={secondaryColor}
                                            fillOpacity={0.85}
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="clean_area"
                                            name="clean_area"
                                            stroke={primaryColor}
                                            strokeWidth={3}
                                            dot={{
                                                r: 4,
                                                fill: primaryColor,
                                            }}
                                            activeDot={{ r: 6 }}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </ChartCard>
                </ChatCardContainer>
            </div>
        </div>
    );
}

const areaCompareDatumPropType = PropTypes.shape({
    date: PropTypes.string,
    clean_area: PropTypes.number,
    task_area: PropTypes.number,
});

CleaningReportAreaComparisonCard.propTypes = {
    primaryColor: PropTypes.string.isRequired,
    secondaryColor: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(areaCompareDatumPropType).isRequired,
};

export default injectIntl(CleaningReportAreaComparisonCard);
